// @ts-strict-ignore
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { ColumnDef } from '@usealloy/component-library';
import {
  Badge, Icon, IconName, Token,
} from '@usealloy/component-library';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import { PublishedAttributeOwnerType } from './types';
import type {
  PublishedAttributeFormatted,
  PublishedAttributeUnformatted,
  PublishedAttributeGroupingUnformatted,
  PublishedAttributeGroupingFormatted,
} from './types';
import type { FeatureFlags } from '~/helpers/typescript';
import { tooltipZIndexModifiers } from '~/constants/reactstrap';

const VALID_VALUES_RANGE_DATA_TYPES = ['integer', 'decimal', 'date', 'timestamp'];
const VALID_VALUES_SET_DATA_TYPES = ['string'];

export const formatObjectType = (objectType: string): string => {
  if (!objectType) return 'Entity';
  return objectType.charAt(0).toUpperCase() + objectType.slice(1);
};

export const getDaysFromSeconds = (seconds: number): number | null => (
  seconds ? dayjs.duration?.({ seconds }).as('days') : null
);

export const getSecondsFromDays = (days: number): number | null => (
  days ? dayjs.duration?.({ days }).as('seconds') : null
);

export const coerceDefaultValue = (
  defaultValue: string,
  dataType: string,
): string | number | boolean => {
  switch (dataType) {
    case 'integer':
      return parseInt(defaultValue, 10);
    case 'decimal':
      return parseFloat(defaultValue);
    case 'boolean':
      return defaultValue === 'true';
    default:
      break;
  }

  return defaultValue;
};

export const coerceValidValue = (
  validValue: string | null,
  dataType: string,
): string | number | null => {
  switch (dataType) {
    case 'integer':
      return parseInt(validValue, 10);
    case 'decimal':
      return parseFloat(validValue);
    case 'date':
    case 'timestamp':
      return validValue || null;
    default:
      break;
  }

  return validValue;
};

export const getArrayFromCsvString = (csvString: string): string[] => {
  if (!csvString) return [];

  const array = csvString.split(',');

  return array.reduce((acc, curr) => {
    const trimmed = curr.trim();
    if (trimmed) acc.push(trimmed);
    return acc;
  }, []);
};

export const getFormattedPublishedAttributes = (
  rawAttributesData: PublishedAttributeUnformatted[],
  featureFlags: Partial<FeatureFlags> = {},
): PublishedAttributeFormatted[] => {
  const formattedAttributes = rawAttributesData.map(attributeItem => {
    const {
      published_attribute_token: attributeToken,
      published_attribute_name: attributeName,
      data_type: dataType,
      default_value: defaultValue,
      default_value_note: defaultValueNote,
      description,
      value_expires_in: valueExpiresInSeconds,
      contains_pii: containsPii,
      jq_filter: jqFilter,
      archived,
      active_workflows: activeWorkflows,
      owner_type: ownerType,
      object_type: objectType,
      valid_value_min: validValueMin,
      valid_value_max: validValueMax,
      valid_values: validValuesArray,
      agent_can_create_value: agentCanCreateValue,
      _embedded: {
        grouping,
      },
    } = attributeItem;
    const hasExpiration = Boolean(valueExpiresInSeconds);
    const hasJqFilter = Boolean(jqFilter);

    const payload: PublishedAttributeFormatted = {
      attributeName,
      attributeToken,
      dataType: dataType as any,
      defaultValue: defaultValue.toString(),
      defaultValueNote: defaultValueNote || '',
      description: description || '',
      valueExpiresInDays: hasExpiration ? getDaysFromSeconds(valueExpiresInSeconds) : undefined,
      containsPii,
      jqFilter: hasJqFilter ? jqFilter : undefined,
      archived,
      activeWorkflows: activeWorkflows || [],
      hasExpiration,
      hasJqFilter,
      ownerType,
      objectType,
      groupingToken: grouping?.grouping_token || '',
    };

    if (featureFlags?.paValidationImprovements) {
      payload.validValueMin = !isNil(validValueMin) ? validValueMin.toString() : '';
      payload.validValueMax = !isNil(validValueMax) ? validValueMax.toString() : '';
      payload.validValues = (validValuesArray || []).join(',');
      payload.agentCanCreateValue = agentCanCreateValue;
    }

    return payload;
  }).sort(({
    attributeName: attributeNameA,
  }, {
    attributeName: attributeNameB,
  }) => attributeNameA.localeCompare(attributeNameB));

  return formattedAttributes;
};

export const getPublishedAttributesAddPayload = (
  attributeData: Partial<PublishedAttributeFormatted>,
  featureFlags: Partial<FeatureFlags> = {},
): Partial<PublishedAttributeUnformatted> => {
  const {
    attributeName,
    objectType,
    dataType,
    defaultValue,
    defaultValueNote,
    description,
    containsPii,
    valueExpiresInDays,
    jqFilter,
    hasExpiration,
    hasJqFilter,
    validValueMin,
    validValueMax,
    validValues: validValuesCsvString,
    agentCanCreateValue,
    groupingToken,
  } = attributeData;

  const validValuesArray = getArrayFromCsvString(validValuesCsvString);

  const payload: Partial<PublishedAttributeUnformatted> = {
    published_attribute_name: attributeName,
    data_type: dataType,
    default_value: coerceDefaultValue(defaultValue, dataType),
    default_value_note: defaultValueNote || null,
    description: description || null,
    contains_pii: containsPii,
    object_type: objectType || 'entity',
    value_expires_in: hasExpiration ? getSecondsFromDays(valueExpiresInDays) : undefined,
    jq_filter: hasJqFilter ? jqFilter : undefined,
  };

  if (featureFlags?.paValidationImprovements) {
    payload.agent_can_create_value = agentCanCreateValue;

    if (VALID_VALUES_RANGE_DATA_TYPES.includes(dataType)) {
      payload.valid_value_min = coerceValidValue(validValueMin, dataType) ?? null;
      payload.valid_value_max = coerceValidValue(validValueMax, dataType) ?? null;
    } else if (VALID_VALUES_SET_DATA_TYPES.includes(dataType)) {
      payload.valid_values = validValuesArray.length ? validValuesArray : null;
    }
  }

  if (featureFlags?.groupedPublishedAttributes) {
    payload.grouping_token = groupingToken || null;
  }

  return payload;
};

export const getPublishedAttributesEditPayload = (
  attributeData: Partial<PublishedAttributeFormatted>,
  featureFlags: Partial<FeatureFlags> = {},
): Partial<PublishedAttributeUnformatted> => {
  const {
    attributeName,
    dataType,
    defaultValue,
    defaultValueNote,
    description,
    valueExpiresInDays,
    jqFilter,
    archived,
    hasExpiration,
    hasJqFilter,
    validValueMin,
    validValueMax,
    validValues: validValuesCsvString,
    agentCanCreateValue,
    groupingToken,
  } = attributeData;

  const validValuesArray = getArrayFromCsvString(validValuesCsvString);

  const payload: Partial<PublishedAttributeUnformatted> = {
    published_attribute_name: attributeName,
    default_value: coerceDefaultValue(defaultValue, dataType),
    default_value_note: defaultValueNote || null,
    description: description || null,
    value_expires_in: hasExpiration ? getSecondsFromDays(valueExpiresInDays) : null,
    jq_filter: hasJqFilter ? jqFilter : null,
    archived,
  };

  if (featureFlags?.paValidationImprovements) {
    payload.agent_can_create_value = agentCanCreateValue;

    if (VALID_VALUES_RANGE_DATA_TYPES.includes(dataType)) {
      payload.valid_value_min = coerceValidValue(validValueMin, dataType) ?? null;
      payload.valid_value_max = coerceValidValue(validValueMax, dataType) ?? null;
    } else if (VALID_VALUES_SET_DATA_TYPES.includes(dataType)) {
      payload.valid_values = validValuesArray.length ? validValuesArray : null;
    }
  }

  if (featureFlags?.groupedPublishedAttributes) {
    payload.grouping_token = groupingToken || null;
  }

  return payload;
};

interface DimmableTableCellProps {
  dimmed: boolean;
  children: React.ReactNode;
}

const DimmableTableCell: React.FC<DimmableTableCellProps> = ({ dimmed, children }) => {
  const style = {
    justifyContent: 'flex-start',
    color: dimmed ? 'var(--cl-gray-700)' : undefined,
  };

  return (
    <span
      style={style}
    >
      {children}
    </span>
  );
};

interface ArchivableTableCellProps {
  row: any;
  dimmed?: boolean;
  children?: React.ReactNode;
}

const ArchivableTableCell: React.FC<ArchivableTableCellProps> = ({
  row,
  dimmed = false,
  children = null,
}) => {
  const { archived } = row.row.original;

  return (
    <DimmableTableCell
      dimmed={dimmed || archived}
    >
      {children || row.getValue()}
    </DimmableTableCell>
  );
};

export const getPublishedttributeTableColumns = (
  showArchivedColumn: boolean = false,
  featureFlags: Partial<FeatureFlags> = {},
): ColumnDef<PublishedAttributeFormatted>[] => {
  const columns = [{
    header: 'Attribute Name',
    accessorKey: 'attributeName',
    cell: row => <ArchivableTableCell row={row} />,
    maxSize: 200,
  },
  {
    header: 'Object',
    accessorKey: 'objectType',
    cell: row => {
      const objectType = row.getValue() as string;
      const displayValue = formatObjectType(objectType);
      return <ArchivableTableCell row={row}>{displayValue}</ArchivableTableCell>;
    },
    maxSize: 120,
  },
  {
    header: 'Data Type',
    accessorKey: 'dataType',
    cell: row => <ArchivableTableCell row={row} />,
    maxSize: 100,
  },
  {
    header: 'PA Token',
    accessorKey: 'paToken',
    cell: row => <Token displayContent={row.row.original.attributeToken} />,
    maxSize: 120,
  },
  {
    header: 'Default',
    accessorKey: 'defaultValue',
    cell: row => {
      let defaultValue = row.getValue();
      let emptyString = false;
      const { dataType, defaultValueNote } = row.row.original;

      if (dataType === 'date') {
        defaultValue = defaultValue.split('T').shift();
      }

      if (dataType === 'string' && !defaultValue) {
        emptyString = true;
        defaultValue = '(empty string)';
      }

      if (!defaultValueNote) {
        return (
          <ArchivableTableCell row={row} dimmed={emptyString}>
            {defaultValue}
          </ArchivableTableCell>
        );
      }

      const iconId = `i${uuidv4()}`;

      return (
        <ArchivableTableCell row={row} dimmed={emptyString}>
          <span style={{ marginRight: '0.5rem' }}>{defaultValue}</span>
          <Icon icon={IconName.Info} id={iconId} />
          <Tooltip
            target={iconId}
            placement="bottom"
            delay={0}
            modifiers={tooltipZIndexModifiers}
          >
            {defaultValueNote}
          </Tooltip>
        </ArchivableTableCell>
      );
    },
    maxSize: 125,
  },
  {
    header: 'Expiration',
    accessorKey: 'valueExpiresInDays',
    cell: row => {
      const value = row.getValue();

      return (
        <ArchivableTableCell row={row}>
          {value ? `${value} day${value === 1 ? '' : 's'}` : ''}
        </ArchivableTableCell>
      );
    },
    maxSize: 120,
  },
  ...featureFlags?.paValidationImprovements
    ? [{
      header: 'Update in Dashboard',
      accessorKey: 'agentCanCreateValue',
      cell: row => (
        <ArchivableTableCell row={row}>
          {row.getValue() === true ? 'Yes' : 'No'}
        </ArchivableTableCell>
      ),
    }]
    : [],
  {
    header: 'PII',
    accessorKey: 'containsPii',
    cell: row => (
      <ArchivableTableCell row={row}>
        {row.getValue() === true ? 'Yes' : 'No'}
      </ArchivableTableCell>
    ),
    maxSize: 75,
  },
  {
    header: 'JQ on Inputs',
    accessorKey: 'jqFilter',
    maxSize: 120,
    cell: row => {
      const jqFilterValue = row.getValue() as string;
      if (jqFilterValue) {
        /*
        For querySelector to work, id cannot start with a digit
        See details at https://stackoverflow.com/a/37271406
        */
        const iconId = `i${uuidv4()}`;
        return (
          <ArchivableTableCell row={row}>
            <span style={{ marginRight: '0.5rem' }}>Yes</span>
            <Icon icon={IconName.Info} id={iconId} />
            <Tooltip
              target={iconId}
              placement="bottom"
              delay={0}
              modifiers={tooltipZIndexModifiers}
            >
              {jqFilterValue}
            </Tooltip>
          </ArchivableTableCell>
        );
      }

      return (
        <ArchivableTableCell row={row}>No</ArchivableTableCell>
      );
    },
  },
  {
    id: 'description',
    header: 'Description',
    accessorKey: 'description',
    cell: row => <ArchivableTableCell row={row} />,
  }];

  if (showArchivedColumn) {
    // @ts-ignore conditionally including column
    columns.push({
      id: 'archived',
      header: '',
      accessorKey: 'archived',
      maxSize: 100,
      cell: row => {
        const isArchived = row.getValue() as boolean;
        if (isArchived === true) {
          return (
            <Badge>Archived</Badge>
          );
        }
        return null;
      },
    });
  }

  return columns;
};

export const getPublishedAttributeGroupingsTableColumns = (
  showArchivedColumn: boolean = false,
): ColumnDef<PublishedAttributeGroupingFormatted>[] => {
  const columns = [{
    header: 'Order',
    accessorKey: 'groupingOrder',
    accessorFn: row => row?.groupingOrder || '-',
    cell: row => <ArchivableTableCell row={row} />,
    maxSize: 50,
  },
  {
    header: 'Name',
    accessorKey: 'groupingName',
    cell: row => <ArchivableTableCell row={row} />,
    maxSize: 150,
  },
  {
    header: 'Count',
    accessorFn: row => row?.publishedAttributes?.length || 0,
    cell: row => <ArchivableTableCell row={row} />,
    maxSize: 50,
  },
  {
    header: 'Attributes',
    accessorFn: row => (row?.publishedAttributes || []).map(pa => pa.name).join(', '),
    cell: row => <ArchivableTableCell row={row} />,
  },
  ];

  if (showArchivedColumn) {
    // @ts-ignore conditionally including column
    columns.push({
      header: '',
      accessorKey: 'archived',
      maxSize: 50,
      cell: row => {
        const isArchived = row.getValue() as boolean;
        if (isArchived === true) {
          return (
            <Badge>Archived</Badge>
          );
        }
        return null;
      },
    });
  }

  return columns;
};

export const getFormattedPublishedAttributeGroupings = (
  rawGroupingsData: PublishedAttributeGroupingUnformatted[],
) => {
  if (!rawGroupingsData) {
    return [];
  }

  const formattedGroupings = rawGroupingsData.map(groupingItem => {
    const {
      grouping_token: groupingToken,
      name: groupingName,
      archived,
      created_at: createdAt,
      order: groupingOrder,
      _embedded: {
        published_attributes: publishedAttributes,
      },
    } = groupingItem;

    const payload: PublishedAttributeGroupingFormatted = {
      groupingToken,
      groupingName,
      archived,
      createdAt,
      groupingOrder,
      publishedAttributes: publishedAttributes.map(pa => ({
        name: pa.name,
        token: pa.published_attribute_token,
      })),
    };

    return payload;
  });

  return formattedGroupings;
};

export const getPublishedAttributeGroupingsAddPayload = (
  groupingData: Partial<PublishedAttributeGroupingFormatted>,
): Partial<PublishedAttributeGroupingUnformatted> => {
  const {
    groupingName,
    groupingOrder,
    groupingAttributeTokens,
    archived,
  } = groupingData;

  const payload: Partial<PublishedAttributeGroupingUnformatted> = {
    name: groupingName,
    order: groupingOrder,
    archived: typeof archived === 'boolean' ? archived : null,
    published_attribute_tokens: groupingAttributeTokens,
  };

  return payload;
};

export const tableArchiveFilterModes = {
  notArchived: 'Not Archived',
  archived: 'Archived',
  all: 'All',
};

export function isSystemPublishedAttribute({
  publishedAttribute,
}: {
  publishedAttribute: PublishedAttributeFormatted;
}) {
  return publishedAttribute.ownerType === PublishedAttributeOwnerType.SYSTEM;
}

export default {
  getFormattedPublishedAttributes,
  getPublishedttributeTableColumns,
  getSecondsFromDays,
  isSystemPublishedAttribute,
  tableArchiveFilterModes,
};
